import { ref } from "vue";
import { CANVAS_SIZE } from "../../constants/canvas";

interface Point {
  x: number;
  y: number;
}

// Resolution mapping based on canvas size
const RESOLUTION_MAPPING = {
  1024: { base: 0.003, scale: 48 },
  512: { base: 0.005, scale: 32 },
  256: { base: 0.008, scale: 24 },
  128: { base: 0.012, scale: 16 },
  64: { base: 0.016, scale: 12 },
  32: { base: 0.02, scale: 8 },
} as const;

// Get resolution parameters based on canvas size
function getResolutionParams(canvasSize: number) {
  const closest = Object.keys(RESOLUTION_MAPPING)
    .map(Number)
    .reduce((prev, curr) => {
      return Math.abs(curr - canvasSize) < Math.abs(prev - canvasSize)
        ? curr
        : prev;
    });
  return RESOLUTION_MAPPING[closest as keyof typeof RESOLUTION_MAPPING];
}

// Collection of points to control the Bezier curve
export class DrawingPoints {
  private points: Point[] = [];
  private readonly maxPoints: number = 3; // Limit control points to 3 for quadratic Bezier curves
  private lutCache: { x: Float32Array; y: Float32Array } | null = null;
  private lastPoints: Point[] = [];
  private readonly maxDistance: number = 1;

  constructor(private readonly pixelSize: number) { }

  private calculateDistance(p1: Point, p2: Point): number {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Add a new control point, maintaining maximum points limit
  addPoint(point: Point): boolean {
    if (this.points.length > 0) {
      const lastPoint = this.points[this.points.length - 1];
      const distance = this.calculateDistance(lastPoint, point);

      // If distance exceeds threshold, add interpolated points
      if (distance > this.maxDistance * this.pixelSize) {
        const steps = Math.ceil(distance / (this.maxDistance * this.pixelSize));
        for (let i = 1; i < steps; i++) {
          const t = i / steps;
          const interpolatedPoint = {
            x: lastPoint.x + (point.x - lastPoint.x) * t,
            y: lastPoint.y + (point.y - lastPoint.y) * t,
          };
          this.points.push(interpolatedPoint);
          if (this.points.length > this.maxPoints) {
            this.points.shift();
          }
        }
      }
    }

    this.points.push(point);
    if (this.points.length > this.maxPoints) {
      this.points.shift();
    }
    this.lutCache = null;
    return true;
  }

  // Clear all control points
  clear() {
    this.points = [];
    this.lutCache = null;
    this.lastPoints = [];
  }

  // Get current control points
  getPoints(): Point[] {
    return this.points;
  }

  // Check if there are enough points to draw a curve
  hasEnoughPoints(): boolean {
    return this.points.length >= 2;
  }

  // Get or create LUT for current points
  getLUT(resolution: number): { x: Float32Array; y: Float32Array } {
    // Check if we can reuse the cached LUT
    if (this.lutCache && this.pointsEqual(this.lastPoints, this.points)) {
      return this.lutCache;
    }

    const lutSize = Math.ceil(1 / resolution);
    const lut = {
      x: new Float32Array(lutSize),
      y: new Float32Array(lutSize),
    };

    // Pre-calculate bezier points
    for (let i = 0; i < lutSize; i++) {
      const t = i * resolution;
      const point = this.calculateBezierPoint(t);
      lut.x[i] = point.x;
      lut.y[i] = point.y;
    }

    // Cache the result
    this.lutCache = lut;
    this.lastPoints = [...this.points];
    return lut;
  }

  private pointsEqual(points1: Point[], points2: Point[]): boolean {
    if (points1.length !== points2.length) return false;
    return points1.every((p1, i) => {
      const p2 = points2[i];
      return p1.x === p2.x && p1.y === p2.y;
    });
  }

  private calculateBezierPoint(t: number): Point {
    if (this.points.length === 2) {
      // Linear Bezier (straight line)
      return {
        x: this.points[0].x + (this.points[1].x - this.points[0].x) * t,
        y: this.points[0].y + (this.points[1].y - this.points[0].y) * t,
      };
    } else if (this.points.length === 3) {
      // Quadratic Bezier curve with optimized calculations
      const mt = 1 - t;
      const mt2 = mt * mt;
      const t2 = t * t;
      const mt_2t = 2 * mt * t;

      return {
        x:
          mt2 * this.points[0].x +
          mt_2t * this.points[1].x +
          t2 * this.points[2].x,
        y:
          mt2 * this.points[0].y +
          mt_2t * this.points[1].y +
          t2 * this.points[2].y,
      };
    }
    return this.points[0];
  }
}

// Pre-calculate brush patterns for common brush sizes
const brushPatternCache = new Map<number, Int32Array>();

// Helper function to calculate pixels within brush area
function fillBrushArea(
  centerX: number,
  centerY: number,
  brushSize: number,
  width: number
): number[] {
  // Ensure brush size is at least 1 pixel
  const effectiveBrushSize = Math.max(1, Math.min(brushSize, CANVAS_SIZE / 4));
  const radius = Math.max(0, Math.floor(effectiveBrushSize / 2));

  // Check if we have a cached pattern for this brush size
  let pattern = brushPatternCache.get(effectiveBrushSize);
  if (!pattern) {
    const pixels: number[] = [];
    const radiusSquared = radius * radius;

    // Pre-calculate the pattern for this brush size
    for (let y = -radius; y <= radius; y++) {
      const y2 = y * y;
      for (let x = -radius; x <= radius; x++) {
        // For brush size 1, only add the center pixel
        if (effectiveBrushSize === 1) {
          if (x === 0 && y === 0) {
            pixels.push(0);
          }
        } else {
          // Use squared distance for more accurate circular brush
          if (x * x + y2 <= radiusSquared) {
            pixels.push(y * width + x);
          }
        }
      }
    }
    pattern = new Int32Array(pixels);
    brushPatternCache.set(effectiveBrushSize, pattern);
  }

  // Apply the pattern to the current center position using TypedArray for better performance
  const result = new Int32Array(pattern.length);
  const centerOffset = centerY * width + centerX;
  for (let i = 0; i < pattern.length; i++) {
    const offset = pattern[i];
    const pixelIndex = centerOffset + offset;
    const x = pixelIndex % width;
    const y = Math.floor(pixelIndex / width);

    if (x >= 0 && x < width && y >= 0 && y < width) {
      result[i] = pixelIndex;
    }
  }

  return Array.from(result.filter((x) => x > 0));
}

// Main drawing composition function
export function useBezierDrawing(width: number, pixelSize: number) {
  const drawingPoints = new DrawingPoints(pixelSize);
  const lastDrawnPixel = ref<Point | null>(null);
  const pixelSet = new Set<number>(); // Use Set for efficient deduplication

  // Convert actual coordinates to canvas pixel coordinates
  function toPixelCoord(coord: number): number {
    return Math.floor(coord / pixelSize);
  }

  // Check if coordinates are within canvas bounds
  function isInBounds(x: number, y: number): boolean {
    return x >= 0 && x < width && y >= 0 && y < width;
  }

  // Draw Bezier curve with the current control points
  function drawBezierCurve(
    onDrawPixel: (indices: number[]) => void,
    brushSize: number
  ) {
    const points = drawingPoints.getPoints();
    if (!points.length) return;

    pixelSet.clear();

    // Calculate resolution based on brush size and canvas size with dynamic parameters
    const { base, scale } = getResolutionParams(CANVAS_SIZE);
    const resolution = Math.max(
      base, // minimum resolution based on canvas size
      base * (1 / Math.max(1, brushSize / 2)) * (scale / width)
    );

    // Get pre-calculated LUT for current points
    const lut = drawingPoints.getLUT(resolution);
    const lutSize = lut.x.length;

    // Batch collect pixels that need updating
    for (let i = 0; i < lutSize; i++) {
      const pixelX = toPixelCoord(lut.x[i]);
      const pixelY = toPixelCoord(lut.y[i]);

      if (isInBounds(pixelX, pixelY)) {
        const areaPixels = fillBrushArea(pixelX, pixelY, brushSize, width);
        for (const pixel of areaPixels) {
          pixelSet.add(pixel);
        }
      }
    }

    // Update all collected pixels at once
    if (pixelSet.size > 0) {
      onDrawPixel(Array.from(pixelSet));
    }
  }

  // Add a new drawing point and update the canvas
  function addDrawingPoint(
    x: number,
    y: number,
    onDrawPixel: (indices: number[]) => void,
    brushSize: number
  ) {
    const point = { x, y };
    const shouldDrawCurve = drawingPoints.addPoint(point);

    if (shouldDrawCurve && drawingPoints.hasEnoughPoints()) {
      drawBezierCurve(onDrawPixel, brushSize);
    } else {
      const pixelX = toPixelCoord(x);
      const pixelY = toPixelCoord(y);
      if (isInBounds(pixelX, pixelY)) {
        const areaPixels = fillBrushArea(pixelX, pixelY, brushSize, width);
        onDrawPixel(areaPixels);
      }
    }

    lastDrawnPixel.value = point;
  }

  // Reset drawing state
  function resetDrawing() {
    drawingPoints.clear();
    lastDrawnPixel.value = null;
    pixelSet.clear();
  }

  return {
    addDrawingPoint,
    resetDrawing,
  };
}
